/* Кадр разового фото «＋ Фото» (§20.1 п. 5, bot-dance.js cropHTML): рамка в пропорциях ролика (без ролика — 9:16) с меткой формата словом,
   снимок двигают пальцем, ползунок приближает; «Двигайте фото: в кадре лицо и тело», «Готово · Отмена» и «Другое фото».
   Кадр — центр (crop_x, crop_y) в долях снимка и приближение zoom; уходит, только если форма публикует эти поля. */
import { useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { uiText } from '../../i18n';
import { ToolIcon } from '../studio/circle/ToolIcon';

import { CROP_CY, cropBox, cropStyle, frameSize, type MotionCropValue } from './motion-crop-model';

export function MotionCrop({
  url,
  ratio,
  ratioWord,
  initial,
  disabled,
  onDone,
  onCancel,
  onOther,
}: {
  url: string;
  ratio: number;
  ratioWord: string;
  initial: MotionCropValue | null;
  disabled?: boolean;
  onDone(value: MotionCropValue): void;
  onCancel(): void;
  onOther(file: File): void;
}) {
  const [image, setImage] = useState<{ w: number; h: number } | null>(null);
  const [value, setValue] = useState<MotionCropValue>(initial ?? { cx: 0.5, cy: CROP_CY, z: 1 });
  const drag = useRef<{ x: number; y: number; start: MotionCropValue } | null>(null);
  const size = frameSize(ratio);
  const clamp = (next: MotionCropValue) => {
    if (!image) return next;
    const b = cropBox(image, ratio, next);
    return { cx: b.cx, cy: b.cy, z: next.z };
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || !image) return;
    const b = cropBox(image, ratio, d.start);
    setValue(
      clamp({
        cx: d.start.cx - (event.clientX - d.x) / (size.w * b.wP),
        cy: d.start.cy - (event.clientY - d.y) / (size.h * b.hP),
        z: d.start.z,
      }),
    );
  };
  return (
    <div className="motion-crop">
      <div className="pf-crop dn-crop">
        <div
          className="pf-cf"
          style={{ width: size.w, height: size.h, touchAction: 'none' }}
          onPointerDown={event => {
            drag.current = { x: event.clientX, y: event.clientY, start: value };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={move}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
        >
          <img
            id="dnCi"
            src={url}
            alt={uiText('Кадр фото')}
            draggable={false}
            onLoad={event => setImage({ w: event.currentTarget.naturalWidth, h: event.currentTarget.naturalHeight })}
            style={image ? cropStyle(image, ratio, value) : { width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <span className="pf-cr">{uiText(ratioWord)}</span>
        </div>
      </div>
      <div className="ap-under">
        <div className="czoom">
          <span className="zi">
            <ToolIcon kind="image" />
          </span>
          <input
            className="range"
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={value.z}
            style={{ '--p': `${(((value.z - 1) / 2) * 100).toFixed(1)}%` } as CSSProperties}
            aria-label={uiText('Приближение')}
            onChange={event => setValue(v => clamp({ ...v, z: Number(event.target.value) }))}
          />
          <span className="zi big">
            <ToolIcon kind="image" />
          </span>
        </div>
        <p className="ap-hint">{uiText('Двигайте фото: в кадре лицо и тело')}</p>
        <div className="ap-acts">
          <button type="button" className="btn" disabled={disabled} onClick={() => onDone(clamp(value))}>
            <ToolIcon kind="check" />
            {uiText('Готово')}
          </button>
          <button type="button" className="btn line" onClick={onCancel}>
            <ToolIcon kind="close" />
            {uiText('Отмена')}
          </button>
        </div>
        <label className="lnk dn-oth">
          <input
            type="file"
            accept="image/jpeg,image/png"
            hidden
            disabled={disabled}
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) {
                setImage(null);
                setValue({ cx: 0.5, cy: CROP_CY, z: 1 });
                onOther(file);
              }
            }}
          />
          <ToolIcon kind="refresh" />
          <span>{uiText('Другое фото')}</span>
        </label>
      </div>
    </div>
  );
}
